<template>
  <div
    v-if="brandSneakPeek"
    class="sneak-peek-info"
  >
    <div class="sneak-peek-info__header">
      <img
        class="sneak-peek-info__image"
        :alt="brandSneakPeek.brand_name"
        :src="getImage()"
      >
      <AkHeading2 class="sneak-peek-info__title">
        {{ brandSneakPeek.brand_name }}
      </AkHeading2>
    </div>
    <div
      v-if="brandSneakPeek"
      class="sneak-peek-info__content"
    >
      <div class="sneak-peek-info__divider" />
      <div class="sneak-peek-info__infos">
        <div class="sneak-peek-info__social-link">
          <a
            v-if="brandSneakPeek.instagram_followers"
            :href="instagramLink"
            class="sneak-peek-info__link"
            @click="trackInstagramLinkClick(brandSneakPeek, carouselUuid)"
          >
            <AkIcon
              class="sneak-peek-info__icon"
              symbol="instagram"
            />
            {{ followers }}
          </a>
          <span class="sneak-peek-info__location">
            <AkIcon
              class="sneak-peek-info__icon"
              symbol="geo-alt"
            />
            {{ country?.name }}
          </span>
        </div>
      </div>
      <div class="sneak-peek-info__divider" />
      <div class="sneak-peek-info__description">
        <p class="sneak-peek-info__text">
          {{ brandSneakPeek.story }}
        </p>
        <div class="sneak-peek-info__button-wrapper">
          <router-link
            :to="brandSneakPeek.brand_link"
            @click="trackBrandHighlightedClick(brandSneakPeek, carouselUuid)"
          >
            <AkButton
              class="sneak-peek-info__button"
              variant="white"
              outlined
            >
              {{ t('discovery.brandSneakPeek.info.visitStore') }}
            </AkButton>
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { AkHeading2, AkIcon, AkButton } from '@ankorstore/design-system';
import { CountryLight } from '@client/monolith/core/types/country';
import { computed } from 'vue';
import { useStore } from 'vuex';
import { SneakPeekBrand } from '@client/modules/discovery/feature/brands-sneak-peek/types/types';
import { useTracking } from '@bc/discovery/feature/brands-sneak-peek/composables/use-tracking';

import { useI18n } from 'vue-i18n';
const props = defineProps<{
  brandSneakPeek: SneakPeekBrand;
  carouselUuid?: string;
}>();
const store = useStore();
const { t } = useI18n();
const { trackBrandHighlightedClick, trackInstagramLinkClick } = useTracking();
const country = computed<CountryLight>(() =>
  store.getters['global/countries/getCountryByIsoCode'](props.brandSneakPeek.country_code)
);
const instagramLink = computed(() =>
  props.brandSneakPeek?.instagram_username ? `https://www.instagram.com/${props.brandSneakPeek.instagram_username}` : '/'
);

const followers = computed(() => {
  return `${props.brandSneakPeek.instagram_followers} ${t('discovery.brandSneakPeek.followers')}`;
});

const getImage = () => {
  return props.brandSneakPeek.brand_logo;
};
</script>

<style scoped lang="scss">
.sneak-peek-info {
  @apply ds-max-h-full ds-overflow-hidden;

  &__header {
    @apply ds-flex ds-flex-col ds-gap-4 ds-items-center ds-mb-5 lg:ds-flex-row;
  }

  &__title {
    @apply ds-font-bold;
  }

  &__image {
    @apply ds-max-h-[5rem] ds-max-w-[5rem] ds-rounded-full lg:ds-max-h-[4rem] lg:ds-max-w-[4rem];
  }

  &__content {
    @apply ds-flex ds-flex-col ds-gap-4;
  }

  &__text {
    @apply ds-text-neutral-900 ds-text-ellipsis ds-overflow-hidden ds-w-full;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    line-clamp: 3;
  }

  &__button-wrapper {
    @apply ds-flex ds-flex-col ds-w-full ds-justify-center ds-items-center  lg:ds-flex-row lg:ds-justify-start lg:ds-w-fit lg:ds-items-center;
  }

  &__infos {
    @apply ds-flex ds-flex-col ds-gap-2;
  }

  &__social-link {
    @apply ds-flex ds-flex-row ds-gap-5 ds-items-center ds-cursor-pointer;
  }

  &__shipping-info {
    @apply ds-flex ds-flex-row ds-gap-2;
  }

  &__divider {
    @apply ds-w-full ds-h-[1px] ds-bg-neutral-500;
  }

  &__icon {
    @apply ds-w-4 ds-h-4 ds-text-warm-800;
  }

  &__description {
    @apply ds-flex ds-flex-col ds-gap-5 lg:ds-gap-2;
  }
}
</style>
